import React, { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AlertCircle, CheckCircle, Loader, PenLine, Printer } from "lucide-react";
import Seo from "../components/Seo";
import FirmaCanvas from "../components/fotografia/FirmaCanvas";
import { entregaDeAutorizacion, recordarFirma } from "../data/autorizacion";
import TEXTO from "../data/autorizacion-texto.json";
import { SITE_NAME } from "../seo/config";
import { buildWhatsAppUrl } from "../utils/whatsapp";

/**
 * Firma en línea de la autorización de uso de imagen de una entrega.
 *
 * El texto sale de data/autorizacion-texto.json, el mismo archivo con el que
 * send-autorizacion arma el documento que se guarda: lo que se lee aquí es lo
 * que queda firmado. Todo empieza desmarcado; un permiso de imagen no se da
 * por defecto.
 *
 * Al firmar se muestra el comprobante con el texto que devolvió el servidor
 * y un botón para guardarlo en PDF (imprimir → Guardar como PDF). La regla
 * `.comprobante` de styles.css deja solo eso en la hoja.
 */

const ENDPOINT = "/.netlify/functions/send-autorizacion";

const fieldClass =
  "w-full rounded-xl border border-navy-200 bg-white px-4 py-3 text-navy-900 outline-none transition focus:border-navy-500";
const labelClass = "mb-2 block text-sm font-semibold text-navy-900";

const MENSAJE_AYUDA = "Hola Omega, quise firmar la autorización de las fotos y no se envió.";

/* Misma regla que el servidor: lo que parece RUT se valida con su dígito. */
const rutValido = (valor) => {
  const limpio = valor.replace(/[.\s-]/g, "").toUpperCase();
  if (!/^\d{7,8}[\dK]$/.test(limpio)) return false;
  let suma = 0;
  let factor = 2;
  for (let i = limpio.length - 2; i >= 0; i -= 1) {
    suma += Number(limpio[i]) * factor;
    factor = factor === 7 ? 2 : factor + 1;
  }
  const resto = 11 - (suma % 11);
  const dv = resto === 11 ? "0" : resto === 10 ? "K" : String(resto);
  return dv === limpio.slice(-1);
};

const documentoValido = (valor) => {
  const texto = valor.trim();
  if (/^[\d.\s-]+[\dkK]$/.test(texto)) return rutValido(texto);
  return /^[A-Za-z0-9-]{5,20}$/.test(texto.replace(/\s+/g, ""));
};

const usosIniciales = Object.fromEntries(TEXTO.usos.map((uso) => [uso.id, false]));

const AutorizacionPage = () => {
  const { pathname } = useLocation();
  const entrega = entregaDeAutorizacion(pathname);

  const [nombre, setNombre] = useState("");
  const [documento, setDocumento] = useState("");
  const [contacto, setContacto] = useState("");
  const [usos, setUsos] = useState(usosIniciales);
  const [conMenor, setConMenor] = useState(false);
  const [menorNombre, setMenorNombre] = useState("");
  const [menorCalidad, setMenorCalidad] = useState("");
  const [acepta, setAcepta] = useState(false);
  const [firma, setFirma] = useState("");
  const [trampa, setTrampa] = useState("");
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [firmada, setFirmada] = useState(null);

  if (!entrega) return <Navigate to="/" replace />;

  const fecha = entrega.fecha.replace(/ · /g, "-");
  const sesion = `${entrega.titulo} del ${fecha}`;

  const validar = () => {
    if (nombre.trim().split(/\s+/).length < 2) return "Escribe tu nombre y apellido.";
    if (!documentoValido(documento)) return "El RUT no es válido. Revisa el dígito verificador.";
    const digitos = contacto.replace(/\D/g, "");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contacto.trim()) && digitos.length < 8) {
      return "Escribe un correo o un teléfono de contacto.";
    }
    if (!Object.values(usos).some(Boolean)) return "Marca al menos un uso que autorizas.";
    if (conMenor && menorNombre.trim().length < 2) return "Escribe el nombre del menor.";
    if (conMenor && !menorCalidad) return "Indica si eres madre, padre o tutor legal.";
    if (!firma) return "Dibuja tu firma en el recuadro.";
    if (!acepta) return "Marca la declaración para poder firmar.";
    return null;
  };

  const enviar = async (event) => {
    event.preventDefault();
    const problema = validar();
    if (problema) {
      setError({ mensaje: problema });
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const response = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entrega: entrega.path,
          sesion,
          nombre,
          documento,
          contacto,
          usos,
          menor_nombre: conMenor ? menorNombre : "",
          menor_calidad: conMenor ? menorCalidad : "",
          acepta,
          firma,
          sitio_web: trampa,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 400 && data.error) {
        setError({ mensaje: `${data.error}.` });
        return;
      }
      if (!response.ok) throw new Error(data.error || "No se pudo enviar");
      recordarFirma(entrega.path);
      setFirmada({ ...data, firma });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setError({
        mensaje: "No pudimos guardar tu firma. Inténtalo de nuevo en un momento.",
        ayuda: true,
      });
    } finally {
      setEnviando(false);
    }
  };

  const marcarUso = (id) => setUsos((actual) => ({ ...actual, [id]: !actual[id] }));

  return (
    <>
      <Seo
        title={`${TEXTO.titulo} · ${entrega.titulo} ${fecha} | ${SITE_NAME}`}
        description={`Autorización de uso de imagen para la sesión ${sesion}.`}
        path={pathname}
        noindex
      />

      <section className="bg-navy-900 pb-12 pt-28 text-white md:pb-16 md:pt-32">
        <div className="container-page max-w-3xl">
          <p className="text-sand-300">
            {entrega.titulo} · {entrega.fecha}
          </p>
          <h1 className="mt-4 font-display text-display-md font-bold text-white">{TEXTO.titulo}</h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-navy-100">
            {firmada
              ? "Listo. Tu autorización quedó firmada y guardada."
              : "Gracias por querer aparecer en nuestro portafolio. Lee el texto, marca dónde podemos mostrar tus fotos y firma al final. Toma un minuto."}
          </p>
        </div>
      </section>

      <section className="bg-sand-50 py-14 md:py-20">
        <div className="container-page max-w-3xl">
          {firmada ? (
            <div className="comprobante surface-card p-6 sm:p-8">
              <div className="flex items-start gap-3" role="status">
                <CheckCircle className="mt-0.5 h-6 w-6 shrink-0 text-sand-500" aria-hidden="true" />
                <div>
                  <p className="font-display text-xl font-bold text-navy-900">Autorización firmada</p>
                  <p className="mt-1 font-mono text-sm text-navy-500">Folio {firmada.folio}</p>
                </div>
              </div>
              <pre className="mt-6 whitespace-pre-wrap rounded-xl bg-sand-50 p-5 font-serif text-sm leading-relaxed text-navy-900">
                {firmada.texto}
              </pre>
              <p className="mt-5 text-sm font-semibold text-navy-900">Firma</p>
              <img
                src={firmada.firma}
                alt={`Firma de ${nombre}`}
                className="mt-2 w-full max-w-sm rounded-lg border border-navy-100"
              />
              <p className="mt-4 break-all font-mono text-xs text-navy-400">
                Huella SHA-256: {firmada.huella}
              </p>
              <button type="button" onClick={() => window.print()} className="btn-omega mt-6 print:hidden">
                <Printer className="h-4 w-4" aria-hidden="true" />
                Guardar copia en PDF
              </button>
              <p className="mt-4 text-sm text-navy-500 print:hidden">
                Si otra persona que aparece en las fotos también quiere autorizar —tu pareja, o el otro
                padre o madre de un menor— puede firmar con este mismo enlace.
              </p>
            </div>
          ) : (
            <form onSubmit={enviar} noValidate className="surface-card relative p-6 sm:p-8">
              <fieldset>
                <legend className="font-display text-xl font-bold text-navy-900">Tus datos</legend>
                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label htmlFor="aut-nombre" className={labelClass}>
                      Nombre completo
                    </label>
                    <input
                      id="aut-nombre"
                      type="text"
                      maxLength={120}
                      autoComplete="name"
                      value={nombre}
                      onChange={(e) => setNombre(e.target.value)}
                      className={fieldClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="aut-documento" className={labelClass}>
                      RUT <span className="font-normal text-navy-400">(o pasaporte)</span>
                    </label>
                    <input
                      id="aut-documento"
                      type="text"
                      maxLength={20}
                      placeholder="12.345.678-5"
                      value={documento}
                      onChange={(e) => setDocumento(e.target.value)}
                      className={fieldClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="aut-contacto" className={labelClass}>
                      Correo o teléfono
                    </label>
                    <input
                      id="aut-contacto"
                      type="text"
                      maxLength={120}
                      autoComplete="email"
                      value={contacto}
                      onChange={(e) => setContacto(e.target.value)}
                      className={fieldClass}
                    />
                  </div>
                </div>
              </fieldset>

              <fieldset className="mt-8 border-t border-navy-100 pt-8">
                <legend className="font-display text-xl font-bold text-navy-900">La autorización</legend>
                <p className="mt-5 text-navy-600">
                  Sesión: <span className="font-semibold text-navy-900">{sesion}</span>, realizada por
                  Omega Digital.
                </p>
                <p className="mt-4 text-navy-600">{TEXTO.introduccion}</p>
                <div className="mt-4 space-y-3">
                  {TEXTO.usos.map((uso) => (
                    <label
                      key={uso.id}
                      className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 text-sm transition ${
                        usos[uso.id] ? "border-navy-900 bg-navy-50" : "border-navy-200 bg-white hover:border-navy-400"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={usos[uso.id]}
                        onChange={() => marcarUso(uso.id)}
                        className="mt-0.5 h-4 w-4 shrink-0 accent-navy-900"
                      />
                      <span className="font-medium text-navy-900">{uso.texto}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-6 space-y-4 text-sm leading-relaxed text-navy-600">
                  {TEXTO.parrafos.map((parrafo) => (
                    <p key={parrafo.slice(0, 24)}>{parrafo}</p>
                  ))}
                </div>
              </fieldset>

              <fieldset className="mt-8 border-t border-navy-100 pt-8">
                <label className="flex cursor-pointer items-start gap-3 text-sm text-navy-700">
                  <input
                    type="checkbox"
                    checked={conMenor}
                    onChange={(e) => setConMenor(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-navy-900"
                  />
                  <span>
                    <span className="font-semibold text-navy-900">
                      En las fotos aparece un menor de edad a mi cargo
                    </span>
                    <span className="mt-1 block text-navy-500">
                      Firmas también en su representación. Si tiene padre y madre, cada uno firma por
                      separado con este mismo enlace.
                    </span>
                  </span>
                </label>
                {conMenor ? (
                  <div className="mt-5 grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="aut-menor" className={labelClass}>
                        Nombre del menor
                      </label>
                      <input
                        id="aut-menor"
                        type="text"
                        maxLength={120}
                        value={menorNombre}
                        onChange={(e) => setMenorNombre(e.target.value)}
                        className={fieldClass}
                      />
                    </div>
                    <div>
                      <label htmlFor="aut-calidad" className={labelClass}>
                        Firmo como
                      </label>
                      <select
                        id="aut-calidad"
                        value={menorCalidad}
                        onChange={(e) => setMenorCalidad(e.target.value)}
                        className={fieldClass}
                      >
                        <option value="">Elige una opción</option>
                        {TEXTO.calidades.map((calidad) => (
                          <option key={calidad} value={calidad}>
                            {calidad.charAt(0).toUpperCase() + calidad.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : null}
              </fieldset>

              <fieldset className="mt-8 border-t border-navy-100 pt-8">
                <legend className="font-display text-xl font-bold text-navy-900">Firma</legend>
                <div className="mt-5">
                  <FirmaCanvas onChange={setFirma} invalida={error && !firma} />
                </div>
                <label className="mt-5 flex cursor-pointer items-start gap-3 text-sm text-navy-700">
                  <input
                    type="checkbox"
                    checked={acepta}
                    onChange={(e) => setAcepta(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-navy-900"
                  />
                  {TEXTO.cierre}
                </label>
              </fieldset>

              {/* Trampa para bots: fuera de pantalla y fuera del orden de tabulación. */}
              <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
                <label htmlFor="aut-sitio">No llenar este campo</label>
                <input
                  id="aut-sitio"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={trampa}
                  onChange={(e) => setTrampa(e.target.value)}
                />
              </div>

              {error ? (
                <div className="mt-6 flex items-start gap-2 text-sm text-red-700" role="alert">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  <p>
                    {error.mensaje}{" "}
                    {error.ayuda ? (
                      <a
                        href={buildWhatsAppUrl(MENSAJE_AYUDA)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold underline underline-offset-4"
                      >
                        O escríbenos por WhatsApp.
                      </a>
                    ) : null}
                  </p>
                </div>
              ) : null}

              <button type="submit" disabled={enviando} className="btn-omega mt-6">
                {enviando ? (
                  <Loader className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <PenLine className="h-4 w-4" aria-hidden="true" />
                )}
                {enviando ? "Firmando…" : "Firmar autorización"}
              </button>
            </form>
          )}
        </div>
      </section>
    </>
  );
};

export default AutorizacionPage;
